import React, { useState, useEffect } from "react";
import "./App.css";
import Login from "./Login";

// Component Imports for Preschool Management
import PreschoolDashboard from "./components/PreschoolDashboard";
import NewAdmission from "./components/NewAdmission";
import StudentDirectory from "./components/StudentDirectory";
import AttendanceTracker from "./components/AttendanceTracker";
import ClassManagement from "./components/ClassManagement";
import FeeManagement from "./components/FeeManagement";
import StaffDirectory from "./components/StaffDirectory";
import ParentCommunication from "./components/ParentCommunication";
import StudentEnquiryComponent from "./components/StudentEnquiryComponent";
import Reports from "./components/Reports";
import ActivitiesModule from "./components/ActivitiesModule";
import TalkingSketchbookGame from "./components/games/TalkingSketch";
import NumberTracingGame from "./components/games/NumberTracingGame";
import AlphabetTracingGame from "./components/games/AlphabetTracingGame";
import LowercaseAlphabetTracingGame from "./components/games/LowercaseAlphabetTracingGame";
import AlphabetNameLearner from "./components/games/AlphabetNameLearner";
import PhonicsAlphabetLearning from "./components/games/PhonicsAlphabetLearning";
import EnglishAlphabetAscentLearner from "./components/games/EnglishAlphabetAscentLearner";
import CountAndMatchGame from "./components/games/numeracy/CountAndMatchGame";
import CountAndWriteGame from "./components/games/numeracy/CountAndWriteGame";
import CountAndColorBeadsGame from "./components/games/numeracy/CountAndColorBeadsGame";
import BodyPartsGame from "./components/games/BodyPartsGame";

// Icon Imports
import {
  FaHome,
  FaUserPlus,
  FaGraduationCap,
  FaCalendarCheck,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaUsersCog,
  FaComments,
  FaQuestionCircle,
  FaChartBar,
  FaSignOutAlt,
  FaShapes,
  FaPaintBrush,
  FaPencilAlt,
  FaVolumeUp,
  FaGlobeAmericas,
  FaCalculator,
  FaPen,
  FaBars,
  FaTimes,
  FaPalette,
} from "react-icons/fa";

/**
 * Menu Configuration for Preschool ERP Solution
 * Roles: "1" = Admin / Principal, "2" = Teacher / Staff, "3" = Student / Interactive
 */
const MENU_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: FaHome, roles: ["1", "2"] },
  { key: "admissions", label: "New Admission", icon: FaUserPlus, roles: ["1", "2"] },
  { key: "students", label: "Student Directory", icon: FaGraduationCap, roles: ["1", "2"] },
  { key: "attendance", label: "Attendance", icon: FaCalendarCheck, roles: ["1", "2"] },
  { key: "activities", label: "Activities", icon: FaShapes, roles: ["1", "2"] },
  { key: "classes", label: "Classes & Batches", icon: FaChalkboardTeacher, roles: ["1"] },
  { key: "enquiry", label: "Admission Enquiries", icon: FaQuestionCircle, roles: ["1", "2"] },
  { key: "fees", label: "Fee Management", icon: FaMoneyBillWave, roles: ["1"] },
  { key: "communication", label: "Parent Broadcast", icon: FaComments, roles: ["1", "2"] },
  { key: "staff", label: "Staff Directory", icon: FaUsersCog, roles: ["1"] },
  { key: "reports", label: "ERP Reports", icon: FaChartBar, roles: ["1"] },
  { key: "bodyparts", label: "My Body Parts", icon: FaPen, roles: ["1", "2", "3"] },
  { key: "sketchgame", label: "Talking Sketch", icon: FaPaintBrush, roles: ["1", "2", "3"] },
  { key: "numbertracing", label: "Number Tracing", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "alphabettracing", label: "Alphabet Tracing", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "lowercasetracing", label: "Lowercase Tracing", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "spellingstudio", label: "Spelling Studio", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "phonicslearning", label: "Phonics Sounds", icon: FaVolumeUp, roles: ["1", "2", "3"] },
  { key: "accentlearner", label: "Global Alphabets", icon: FaGlobeAmericas, roles: ["1", "2", "3"] },
  { key: "countandmatch", label: "Count & Match", icon: FaCalculator, roles: ["1", "2", "3"] },
  { key: "countandwrite", label: "Count & Write", icon: FaPen, roles: ["1", "2", "3"] },
  { key: "countandcolourbeads", label: "Count & Colour Beads", icon: FaPen, roles: ["1", "2", "3"] },
];

/**
 * Theme Configurations
 */
const THEMES = {
  default: {
    id: "default",
    label: "Default Dark",
    bg: "#111827",
    titleColor: "#FFFFFF",
    subTextColor: "#9CA3AF",
    btnInactiveBg: "#1F2937",
    btnInactiveText: "#F9FAFB",
    btnActiveBg: "#2563EB",
    btnActiveText: "#FFFFFF",
    border: "#374151",
  },
  lightBlue: {
    id: "lightBlue",
    label: "Light Blue",
    bg: "#E0F2FE",
    titleColor: "#0369A1",
    subTextColor: "#0284C7",
    btnInactiveBg: "#F0F9FF",
    btnInactiveText: "#0F172A",
    btnActiveBg: "#0284C7",
    btnActiveText: "#FFFFFF",
    border: "#BAE6FD",
  },
  lightGreen: {
    id: "lightGreen",
    label: "Light Green",
    bg: "#DCFCE7",
    titleColor: "#15803D",
    subTextColor: "#16A34A",
    btnInactiveBg: "#F0FDF4",
    btnInactiveText: "#0F172A",
    btnActiveBg: "#16A34A",
    btnActiveText: "#FFFFFF",
    border: "#BBF7D0",
  },
  lightCream: {
    id: "lightCream",
    label: "Light Cream",
    bg: "#FEF3C7",
    titleColor: "#B45309",
    subTextColor: "#D97706",
    btnInactiveBg: "#FFFBEB",
    btnInactiveText: "#0F172A",
    btnActiveBg: "#D97706",
    btnActiveText: "#FFFFFF",
    border: "#FDE68A",
  },
};

function App() {
  const [token, setToken] = useState(sessionStorage.getItem("token") || null);

  const [clientName, setClientName] = useState(
    sessionStorage.getItem("clientName") || "Wisdom Learning Portal"
  );

  const [clientId, setClientId] = useState(
    sessionStorage.getItem("clientId") || "PRESCHOOL_01"
  );

  const [roleId, setRoleId] = useState(() => {
    const savedRole = sessionStorage.getItem("roleId");
    return savedRole && savedRole !== "undefined" && savedRole !== "null"
      ? savedRole
      : null;
  });

  const [currentThemeKey, setCurrentThemeKey] = useState(
    sessionStorage.getItem("appTheme") || "default"
  );

  const theme = THEMES[currentThemeKey] || THEMES.default;
  const [page, setPage] = useState("dashboard");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);
      if (!isMobile) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleThemeChange = (themeKey) => {
    setCurrentThemeKey(themeKey);
    sessionStorage.setItem("appTheme", themeKey);
  };

  const handleLoginSuccess = (userToken, userRoleId, userClientName, userClientId) => {
    if (!userToken) {
      console.error("Login succeeded but no token was provided!");
      return;
    }

    sessionStorage.setItem("token", userToken);
    setToken(userToken);

    if (userClientName) {
      sessionStorage.setItem("clientName", userClientName);
      setClientName(userClientName);
    }

    if (userClientId) {
      sessionStorage.setItem("clientId", userClientId);
      setClientId(userClientId);
    }

    if (userRoleId !== undefined && userRoleId !== null) {
      const normalizedRole = String(userRoleId);
      sessionStorage.setItem("roleId", normalizedRole);
      setRoleId(normalizedRole);
    } else {
      sessionStorage.removeItem("roleId");
      setRoleId(null);
    }

    setPage("dashboard");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("roleId");
    sessionStorage.removeItem("clientName");
    sessionStorage.removeItem("clientId");

    setToken(null);
    setRoleId(null);
    setClientName("Wisdom Preschool");
    setPage("dashboard");
    setIsMobileMenuOpen(false);
  };

  const handlePageChange = (targetPage) => {
    setPage(targetPage);
    if (isMobileView) {
      setIsMobileMenuOpen(false);
    }
  };

  if (!token || token === "undefined" || token === "null") {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const getFilteredMenuItems = () => {
    return MENU_ITEMS.filter((item) => {
      if (!roleId) return true;
      return item.roles.includes(String(roleId));
    });
  };

  return (
    <div style={styles.appContainer}>
      {/* MOBILE TOP HEADER BAR */}
      <header
        className="mobile-header-bar"
        style={{
          ...styles.mobileHeader,
          backgroundColor: theme.bg,
          color: theme.titleColor,
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <button
          style={{ ...styles.hamburgerButton, color: theme.titleColor }}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle Navigation"
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <span style={styles.mobileTitle}>{clientName}</span>
      </header>

      {/* Mobile Drawer Backdrop */}
      {isMobileView && isMobileMenuOpen && (
        <div
          style={styles.drawerBackdrop}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* SIDEBAR NAVIGATION */}
      <aside
        className={`app-sidebar-drawer ${isMobileMenuOpen ? "open" : ""}`}
        style={{
          ...styles.sidebar,
          backgroundColor: theme.bg,
          borderRight: `1px solid ${theme.border}`,
        }}
      >
        <div style={styles.sidebarHeader}>
          <h2 style={{ ...styles.sidebarTitle, color: theme.titleColor }}>
            {clientName}
          </h2>

          {/* Theme Selector */}
          <div style={{ ...styles.themeStrip, borderTop: `1px solid ${theme.border}` }}>
            <span style={{ ...styles.themeLabel, color: theme.subTextColor }}>
              <FaPalette style={{ marginRight: 4 }} /> Theme:
            </span>
            <div style={styles.themePillsGroup}>
              {Object.keys(THEMES).map((tKey) => {
                const itemTheme = THEMES[tKey];
                const isActive = currentThemeKey === tKey;
                return (
                  <button
                    key={tKey}
                    title={itemTheme.label}
                    className={`theme-pill-btn ${isActive ? "active-theme" : ""}`}
                    style={{
                      backgroundColor: itemTheme.btnActiveBg,
                      borderColor: isActive ? theme.btnActiveBg : "transparent",
                    }}
                    onClick={() => handleThemeChange(tKey)}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <nav style={styles.sidebarNav}>
          {getFilteredMenuItems().map((item) => {
            const IconComponent = item.icon;
            const isActive = page === item.key;
            return (
              <button
                key={item.key}
                style={{
                  ...styles.menuButton,
                  backgroundColor: isActive ? theme.btnActiveBg : theme.btnInactiveBg,
                  color: isActive ? theme.btnActiveText : theme.btnInactiveText,
                  border: `1px solid ${isActive ? theme.btnActiveBg : theme.border}`,
                }}
                onClick={() => handlePageChange(item.key)}
              >
                <IconComponent style={styles.menuIcon} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div style={{ ...styles.sidebarFooter, backgroundColor: theme.bg }}>
          <button style={styles.logoutButton} onClick={handleLogout}>
            <FaSignOutAlt style={styles.menuIcon} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="main-content-area" style={styles.mainContent}>
        <div style={styles.dashboardContentWrapper}>
          {page === "dashboard" && (
            <PreschoolDashboard
              token={token}
              clientId={clientId}
              roleId={roleId}
              onNavigate={(targetPage) => handlePageChange(targetPage)}
            />
          )}

          {page === "admissions" && (
            <NewAdmission token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "students" && (
            <StudentDirectory token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "attendance" && (
            <AttendanceTracker token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "activities" && (
            <ActivitiesModule token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "classes" && (
            <ClassManagement token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "enquiry" && (
            <StudentEnquiryComponent clientId={clientId} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "fees" && (
            <FeeManagement token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "communication" && (
            <ParentCommunication token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "staff" && (
            <StaffDirectory token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {page === "reports" && (
            <Reports token={token} onBack={() => handlePageChange("dashboard")} />
          )}

          {/* Educational Modules */}
          {page === "bodyparts" && <BodyPartsGame />}
          {page === "sketchgame" && <TalkingSketchbookGame />}
          {page === "numbertracing" && <NumberTracingGame />}
          {page === "alphabettracing" && <AlphabetTracingGame />}
          {page === "lowercasetracing" && <LowercaseAlphabetTracingGame />}
          {page === "spellingstudio" && <AlphabetNameLearner />}
          {page === "phonicslearning" && <PhonicsAlphabetLearning />}
          {page === "accentlearner" && <EnglishAlphabetAscentLearner />}
          {page === "countandmatch" && <CountAndMatchGame />}
          {page === "countandwrite" && <CountAndWriteGame />}
          {page === "countandcolourbeads" && <CountAndColorBeadsGame />}
        </div>
      </main>
    </div>
  );
}

const styles = {
  appContainer: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    backgroundColor: "#F1F5F9",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
    position: "relative",
  },
  mobileHeader: {
    display: "none",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "60px",
    alignItems: "center",
    padding: "0 16px",
    zIndex: 900,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  hamburgerButton: {
    background: "none",
    border: "none",
    fontSize: "1.35rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    marginRight: "12px",
  },
  mobileTitle: {
    fontSize: "1.1rem",
    fontWeight: "700",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  drawerBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(2px)",
    zIndex: 999,
  },
  sidebar: {
    width: "280px",
    minWidth: "260px",
    maxWidth: "320px",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    transition: "background-color 0.25s ease, border-color 0.25s ease",
  },
  sidebarHeader: {
    padding: "20px 20px 12px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  sidebarTitle: {
    margin: 0,
    fontSize: "1.35rem",
    fontWeight: "700",
    letterSpacing: "-0.2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  themeStrip: {
    paddingTop: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  themeLabel: {
    fontSize: "0.8rem",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
  },
  themePillsGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  sidebarNav: {
    flex: 1,
    overflowY: "auto",
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    scrollBehavior: "smooth",
  },
  menuButton: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    width: "100%",
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "0.98rem",
    fontWeight: "500",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.15s ease",
  },
  menuIcon: {
    fontSize: "1.1rem",
    minWidth: "20px",
    textAlign: "center",
  },
  sidebarFooter: {
    padding: "16px",
  },
  logoutButton: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "12px 16px",
    backgroundColor: "#EF4444",
    border: "none",
    borderRadius: "10px",
    color: "#FFFFFF",
    fontSize: "0.98rem",
    fontWeight: "600",
    cursor: "pointer",
    textAlign: "left",
  },
  mainContent: {
    flex: 1,
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "#F8FAFC",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  dashboardContentWrapper: {
    padding: "16px",
    width: "100%",
    boxSizing: "border-box",
    flex: 1,
  },
};

export default App;